/**
 * EnvironmentBanner - Sticky top banner showing the current environment/deployment mode.
 * Reads VITE_APP_ENV at build time and renders it as the label (e.g. "Sandbox").
 * Returns null if env var is unset/empty.
 *
 * When VITE_APP_ENV is set, appends VITE_BANNER_TEXT (if set) after the env name
 * using a " — " (em dash) separator. VITE_BANNER_TEXT alone does NOT make the banner
 * appear; the banner only shows when VITE_APP_ENV is set.
 */

import styled from 'styled-components';
import { Server } from 'lucide-react';
import { bannerBaseCss, bannerIconCss, bannerLabelCss } from '../bannerStyles';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const BannerRoot = styled.div`
  ${bannerBaseCss}
  z-index: ${({ theme }) => theme.zIndex.fixed};
  /* Mustard yellow — high-visibility env indicator. White text inherited from
     the shared banner base. */
  background: #d4a017;
  /* Wraps rather than ellipsizing: VITE_BANNER_TEXT carries the PHI warning on
     non-prod, and truncating it would hide the part that matters. The banner is
     in normal flow (sticky, min-height) so extra lines just grow it and push
     content down. */
  overflow-wrap: anywhere;
`;

const ServerIcon = styled(Server)`
  ${bannerIconCss}
`;

const Label = styled.span`
  ${bannerLabelCss}
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const EnvironmentBanner = () => {
  const envValue = (import.meta.env.VITE_APP_ENV ?? '').trim();

  // Return nothing if env var is unset or empty
  if (!envValue) return null;

  // Capitalize first letter of env value
  const capitalizedEnv = envValue.charAt(0).toUpperCase() + envValue.slice(1);

  // Read optional banner text and append with em-dash separator if set
  const bannerText = (import.meta.env.VITE_BANNER_TEXT ?? '').trim();
  const labelContent = bannerText ? `${capitalizedEnv} — ${bannerText}` : capitalizedEnv;

  return (
    <BannerRoot role="region" aria-label={`${capitalizedEnv} environment`}>
      <ServerIcon aria-hidden="true" />
      <Label>{labelContent}</Label>
    </BannerRoot>
  );
};
